/**
 * tableTransformer — ElementTransformer custom pour les tableaux GFM.
 *
 * Pourquoi ? Ni @lexical/markdown ni @lexical/table ne fournissent de
 * transformer markdown pour les tableaux GFM ; le Playground officiel de
 * Lexical en a un custom, qu'on réimplémente ici (avec en plus la prise en
 * charge de la ligne séparateur `| --- | --- |`).
 *
 * Round-trip :
 *
 *   Markdown                         Lexical
 *   ─────────────────────────────────────────────────────
 *   | A | B | C |    ← ligne 1 →    TableNode
 *   | - | - | - |    ← séparateur     └─ TableRowNode (header) [A][B][C]
 *   | 1 | 2 | 3 |    ← data           └─ TableRowNode          [1][2][3]
 *
 * Stratégie d'import (ligne par ligne, c'est ce que permet
 * ElementTransformer) :
 *
 *   1. La regex matche chaque ligne « | ... | ».
 *   2. Si c'est la ligne séparateur (cellules ne contenant que `-`, `:`),
 *      on supprime le paragraphe correspondant — pas de rendu visuel.
 *   3. Sinon, on construit une TableRowNode à partir des cellules.
 *      - Si le sibling précédent est déjà une TableNode → append la row
 *      - Sinon → crée une nouvelle TableNode avec cette row en tant qu'en-tête
 *
 * Stratégie d'export :
 *
 *   On itère les rows : pour chacune, on rend `| cell | cell |`. Après la
 *   première row (header), on insère la ligne séparateur GFM.
 */

import {
  $createParagraphNode,
  $createTextNode,
  type LexicalNode,
} from "lexical";
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import {
  $convertToMarkdownString,
  TRANSFORMERS,
  type ElementTransformer,
} from "@lexical/markdown";

// Une ligne de tableau : commence et finit par `|`, contient au moins un `|`
// intermédiaire. On tolère des espaces avant/après.
const TABLE_ROW_REG_EXP = /^\|(.+)\|\s*$/;

// Une cellule de séparateur : suite de `-` (avec ou sans `:` aux extrémités
// pour l'alignement, ignoré ici car le GFM s'en moque pour le rendu Lexical).
const SEPARATOR_CELL_REG_EXP = /^\s*:?-{1,}:?\s*$/;

/**
 * Découpe le contenu d'une ligne `| a | b | c |` en cellules trimées,
 * en respectant l'échappement `\|` (rare, mais propre).
 */
function splitRowCells(rowContent: string): string[] {
  // Préserve `\|` puis split sur `|` non échappés
  const placeholder = " PIPE ";
  const escaped = rowContent.replace(/\\\|/g, placeholder);
  return escaped
    .split("|")
    .map((cell) => cell.replace(new RegExp(placeholder, "g"), "|").trim());
}

/**
 * Renvoie true si toutes les cellules de la ligne sont des séparateurs GFM.
 */
function isSeparatorRow(cells: readonly string[]): boolean {
  return cells.length > 0 && cells.every((c) => SEPARATOR_CELL_REG_EXP.test(c));
}

/**
 * Construit une TableRowNode à partir d'un tableau de textes de cellules.
 * Le `headerState` détermine si les cellules sont rendues comme en-tête (ROW).
 */
function buildRow(
  cellTexts: readonly string[],
  headerState: number,
): TableRowNode {
  const row = $createTableRowNode();
  for (const text of cellTexts) {
    const cell = $createTableCellNode(headerState);
    const paragraph = $createParagraphNode();
    if (text.length > 0) {
      paragraph.append($createTextNode(text));
    }
    cell.append(paragraph);
    row.append(cell);
  }
  return row;
}

export const TABLE_TRANSFORMER: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],

  // -------------------------------------------------------------------------
  // Export : Lexical → Markdown
  //
  // Détail subtil : le `traverseChildren` fourni par Lexical n'applique QUE
  // les TextFormatTransformers (gras/italique/barré/code inline/lien). Il
  // n'applique PAS les ElementTransformers (listes, citations, paragraphes
  // multiples). Donc si une cellule contient une liste à puces, traverseChildren
  // ressort le texte aplati sans les bullets.
  //
  // Pour préserver TOUS les formatages possibles d'une cellule, on appelle
  // récursivement `$convertToMarkdownString(TRANSFORMERS, cell)` qui ré-exécute
  // l'export complet sur le sous-arbre de la cellule. On passe le TRANSFORMERS
  // de base (sans notre TABLE_TRANSFORMER) car GFM ne supporte de toute façon
  // pas les tableaux imbriqués — ça élimine tout risque de récursion infinie.
  // -------------------------------------------------------------------------
  export: (node: LexicalNode) => {
    if (!$isTableNode(node)) return null;

    const lines: string[] = [];
    const rows = node.getChildren().filter($isTableRowNode);

    rows.forEach((row, rowIndex) => {
      const cells = row.getChildren().filter($isTableCellNode);
      const cellTexts = cells.map((cell) => {
        // Conversion récursive avec TOUS les transformers de base.
        // Résultat possible : "**gras**", "- item\n- item2", "[lien](url)", etc.
        // GFM ne supporte ni le multi-ligne ni les `|` dans une cellule —
        // on aplatit les \n en espaces et on échappe les pipes littéraux.
        return $convertToMarkdownString(TRANSFORMERS, cell)
          .replace(/\r?\n/g, " ")
          .replace(/\|/g, "\\|")
          .trim();
      });

      lines.push(`| ${cellTexts.join(" | ")} |`);

      // Insère la ligne séparateur après le header (première row).
      if (rowIndex === 0) {
        lines.push(`|${" --- |".repeat(cellTexts.length)}`);
      }
    });

    return lines.join("\n");
  },

  // -------------------------------------------------------------------------
  // Import : Markdown → Lexical (ligne par ligne)
  // -------------------------------------------------------------------------
  regExp: TABLE_ROW_REG_EXP,

  replace: (parentNode, _children, match) => {
    const rowContent = match[1];
    if (typeof rowContent !== "string") return;
    const cells = splitRowCells(rowContent);

    // Cas 1 : ligne séparateur GFM → on la fait juste disparaître.
    // Le paragraphe parent est supprimé du DOM Lexical ; la TableNode
    // déjà créée juste avant (par la ligne d'en-tête) reste intacte.
    if (isSeparatorRow(cells)) {
      parentNode.remove();
      return;
    }

    // Cas 2 : sibling précédent est déjà une TableNode → on append la row
    // comme data row (pas d'en-tête).
    const previousSibling = parentNode.getPreviousSibling();
    if ($isTableNode(previousSibling)) {
      const newRow = buildRow(cells, TableCellHeaderStates.NO_STATUS);
      previousSibling.append(newRow);
      parentNode.remove();
      return;
    }

    // Cas 3 : nouvelle table — la ligne courante devient l'en-tête.
    const tableNode = $createTableNode();
    tableNode.append(buildRow(cells, TableCellHeaderStates.ROW));
    parentNode.replace(tableNode);
  },

  type: "element",
};
